/* ─────────────────────────────────────────────
   APEX Program Builder - Core Type Definitions
   ───────────────────────────────────────────── */

// ── Enums / Unions ──────────────────────────

export type MovementPattern =
  | 'squat'
  | 'hinge'
  | 'horizontal_push'
  | 'vertical_push'
  | 'horizontal_pull'
  | 'vertical_pull'
  | 'carry'
  | 'core'
  | 'isolation_upper'
  | 'isolation_lower';

export const MOVEMENT_PATTERN_LABELS: Record<MovementPattern, string> = {
  squat: 'Przysiad',
  hinge: 'Martwy ciąg',
  horizontal_push: 'Pchnięcie poziome',
  vertical_push: 'Pchnięcie pionowe',
  horizontal_pull: 'Ciągnięcie poziome',
  vertical_pull: 'Ciągnięcie pionowe',
  carry: 'Przenoszenie',
  core: 'Brzuch/Core',
  isolation_upper: 'Izolacja (góra)',
  isolation_lower: 'Izolacja (dół)',
};

export type MuscleGroup =
  | 'chest'
  | 'front_delts'
  | 'side_delts'
  | 'rear_delts'
  | 'upper_back'
  | 'lats'
  | 'traps'
  | 'biceps'
  | 'triceps'
  | 'forearms'
  | 'quads'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'abs'
  | 'hip_flexors';

export const MUSCLE_GROUP_LABELS: Record<MuscleGroup, string> = {
  chest: 'Klatka piersiowa',
  front_delts: 'Przód barków',
  side_delts: 'Boki barków',
  rear_delts: 'Tył barków',
  upper_back: 'Górny grzbiet',
  lats: 'Najszersze',
  traps: 'Kaptur',
  biceps: 'Biceps',
  triceps: 'Triceps',
  forearms: 'Przedramiona',
  quads: 'Czworogłowy',
  hamstrings: 'Dwugłowy',
  glutes: 'Pośladki',
  calves: 'Łydki',
  abs: 'Brzuch',
  hip_flexors: 'Zginacze bioder',
};

export const ALL_MUSCLE_GROUPS: MuscleGroup[] = Object.keys(MUSCLE_GROUP_LABELS) as MuscleGroup[];

export type ExerciseTag = 'compound' | 'isolation' | 'accessory' | 'warmup';

export const EXERCISE_TAG_LABELS: Record<ExerciseTag, string> = {
  compound: 'Złożone',
  isolation: 'Izolacja',
  accessory: 'Akcesoryjne',
  warmup: 'Rozgrzewka',
};

export type LoadType = 'rpe' | 'percent_1rm';

export type TrainingLevel = 'beginner' | 'intermediate' | 'advanced' | 'elite';

export type TrainingGoal = 'hypertrophy' | 'strength' | 'power' | 'endurance' | 'general';

export type ProgressionModel = 'linear' | 'undulating' | 'block' | 'autoregulated';

export const TRAINING_LEVEL_LABELS: Record<TrainingLevel, string> = {
  beginner: 'Początkujący (< 1 rok)',
  intermediate: 'Średniozaawansowany (1–3 lata)',
  advanced: 'Zaawansowany (3–7 lat)',
  elite: 'Ekspert (7+ lat)',
};

export const TRAINING_GOAL_LABELS: Record<TrainingGoal, string> = {
  hypertrophy: 'Hipertrofia',
  strength: 'Siła',
  power: 'Moc',
  endurance: 'Wytrzymałość',
  general: 'Ogólna sprawność',
};

export const PROGRESSION_MODEL_LABELS: Record<ProgressionModel, string> = {
  linear: 'Progresja liniowa',
  undulating: 'Falowanie dzienne (DUP)',
  block: 'Periodyzacja blokowa',
  autoregulated: 'Autoregulacja (RPE)',
};

// ── Data Models ─────────────────────────────

/** Exercise from the master database */
export interface ExerciseDefinition {
  id: string;
  name: string;
  movementPattern: MovementPattern;
  primaryMuscle: MuscleGroup;
  secondaryMuscles: MuscleGroup[];
  tag: ExerciseTag;
  defaultSets: number;
  defaultReps: string;
  defaultRPE: number;
  defaultRest: number; // seconds
  defaultTempo: string; // e.g. "3-1-2-0"
}

/** Exercise instance placed in a session */
export interface ExerciseInstance {
  id: string;
  exerciseDefId: string;
  name: string;
  movementPattern: MovementPattern;
  primaryMuscle: MuscleGroup;
  secondaryMuscles: MuscleGroup[];
  tag: ExerciseTag;
  sets: number;
  reps: string;
  tempo: string;
  loadType: LoadType;
  load: number; // RPE value or %1RM
  rest: number; // seconds
  notes: string;
}

export interface Session {
  id: string;
  day: string;
  focus: string;
  warmupExercises: ExerciseInstance[];
  exercises: ExerciseInstance[];
}

export interface Week {
  id: string;
  weekNumber: number;
  sessions: Session[];
  deload: boolean;
}

export interface Plan {
  id: string;
  name: string;
  clientName?: string; // optional - for whom the plan is (client / own use)
  goal: TrainingGoal;
  trainingLevel: TrainingLevel;
  mesocycleLength: number; // weeks
  frequency: number; // sessions per week
  fatigueTolerance: number; // 1–10
  progressionModel: ProgressionModel;
  weeks: Week[];
  createdAt: string;
  updatedAt: string;
}

// ── Analytics / Metrics ─────────────────────

export type VolumeStatus =
  | 'below_mev'
  | 'at_mev'
  | 'optimal'
  | 'approaching_mrv'
  | 'exceeds_mrv';

export interface MuscleVolumeMetric {
  muscle: MuscleGroup;
  directSets: number;
  indirectSets: number;
  totalSets: number;
  mev: number;
  mav: number;
  mrv: number;
  status: VolumeStatus;
}

export interface VolumeMetrics {
  muscleVolumes: MuscleVolumeMetric[];
  totalSets: number;
  compoundSets: number;
  isolationSets: number;
  compoundRatio: number;
  intensityDistribution: {
    low: number;   // RPE ≤6 or ≤65%
    moderate: number; // RPE 7–7.5 or 66–75%
    high: number;     // RPE 8–8.5 or 76–85%
    max: number;      // RPE ≥9 or ≥85%
  };
  volumeLoadScore: number;
}

export interface FatigueMetrics {
  overallFatigue: number; // 0–100
  muscleFatigue: Partial<Record<MuscleGroup, number>>;
  warnings: FatigueWarning[];
  suggestions: string[];
  recoveryIndex: number; // 0–100
}

export interface FatigueWarning {
  severity: 'info' | 'warning' | 'critical';
  message: string;
  muscle?: MuscleGroup;
}

export interface QualityMetrics {
  overallScore: number; // 0–100
  breakdown: {
    pushPullBalance: number;
    movementCoverage: number;
    fatigueManagement: number;
    volumeDistribution: number;
    progressionLogic: number;
  };
  suggestions: QualitySuggestion[];
}

export interface QualitySuggestion {
  category: 'balance' | 'coverage' | 'fatigue' | 'volume' | 'progression';
  priority: 'low' | 'medium' | 'high';
  message: string;
}

export interface CoachSuggestion {
  id: string;
  type: 'missing_pattern' | 'muscle_imbalance' | 'exercise_redundancy' | 'fatigue_risk' | 'progression' | 'general';
  severity: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
  actionable: boolean;
}

// ── Volume Standards ────────────────────────

export interface VolumeStandard {
  muscle: MuscleGroup;
  mev: number;
  mav: number;
  mrv: number;
}

// ── App State ───────────────────────────────

export type AppTab = 'builder' | 'analytics' | 'coach' | 'legend' | 'dashboard' | 'exercises' | 'survey' | 'calculators' | 'terms' | 'privacy' | 'comparison';

// ── Survey System ───────────────────────────

export type SurveyFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'radio'
  | 'checkbox'
  | 'range';

export interface SurveyField {
  id: string;
  type: SurveyFieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];        // for select / multiselect / radio
  min?: number;              // for number / range
  max?: number;              // for number / range
  step?: number;             // for range
  defaultValue?: string;
}

export interface SurveyTemplate {
  id: string;
  trainerId: string;
  title: string;
  description: string;
  fields: SurveyField[];
  createdAt: string;
  updatedAt: string;
  publicToken?: string; // Added for public survey access
}

export interface SurveyResponse {
  id: string;
  surveyId: string;
  trainerId: string;
  respondentName: string;
  respondentEmail?: string;
  answers: Record<string, string | string[] | number | boolean>;
  submittedAt: string;
}

// ── User / Auth ─────────────────────────────

export interface UserProfile {
  id: string;
  name: string;
  role: 'trainer' | 'user';
  pin?: string; // optional simple 4-digit PIN
  createdAt: string;
}

export interface SavedPlanEntry {
  planId: string;
  planName: string;
  clientName: string;
  goal: TrainingGoal;
  updatedAt: string;
  weekCount: number;
  sessionCount: number;
}

export interface PlanTemplate {
  id: string;
  name: string;
  description: string;
  goal: TrainingGoal;
  level: TrainingLevel;
  frequency: number;
  factory: () => Plan;
}
